import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { authConfigured, useMe } from "./oidc";

/**
 * Gate the studio surfaces behind a real OIDC session.
 *
 * Enforcement is active only when OIDC is configured AND it isn't explicitly
 * disabled (VITE_AUTH_ENFORCE="false"). That keeps two escape hatches:
 *  - a checkout without OIDC env runs on demo data (no sign-in to bounce to);
 *  - a developer can set VITE_AUTH_ENFORCE=false to work signed-out locally.
 * Unauthenticated users are sent to /login, remembering where they were headed.
 */
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useMe();
  const location = useLocation();

  const enforce = authConfigured && import.meta.env.VITE_AUTH_ENFORCE !== "false";
  if (!enforce) return <>{children}</>;

  if (isLoading) {
    return (
      <div className="grid min-h-screen place-items-center bg-canvas">
        <div className="flex items-center gap-3 text-muted">
          <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-accent" />
          Checking your session…
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}
